// ============================================================
//  src/pages/community/CommunityPage.js
// ============================================================
import React, {
  useEffect, useState, useCallback, useRef, useContext, createContext,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listCommunityPosts, getCommunityPost,
  createCommunityPost, deleteCommunityPost, resolveCommunityPost,
  likeCommunityPost, createCommunityReply,
  acceptCommunityReply, deleteCommunityReply, getBillingOverview,
} from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import './CommunityPage.css';

// ── helpers ───────────────────────────────────────────────────
function timeAgo(str) {
  if (!str) return '';
  const s = Math.floor((Date.now() - new Date(str.replace(' ','T'))) / 1000);
  if (s < 60)    return 'just now';
  if (s < 3600)  return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}
function initials(name='') {
  return name.split(' ').map(p=>p[0]??'').join('').toUpperCase().slice(0,2);
}
const IMG_BASE = process.env.REACT_APP_API_URL?.replace('/api','') ?? '';

// ── Context (share current user sId down) ────────────────────
const MeCtx = createContext(null);

// ============================================================
//  IMAGE ANNOTATOR
// ============================================================
function ImageAnnotator({ file, onDone, onCancel }) {
  const canvasRef  = useRef(null);
  const [arrows,   setArrows]   = useState([]);   // [{x1,y1,x2,y2}]
  const [drawing,  setDrawing]  = useState(null); // {x1,y1}
  const [imgEl,    setImgEl]    = useState(null);
  const [color,    setColor]    = useState('#ef4444');
  const COLORS = ['#ef4444','#f97316','#eab308','#22c55e','#3b82f6','#a855f7','#fff'];

  // Load image onto canvas
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImgEl(img);
      const cvs = canvasRef.current;
      if (!cvs) return;
      const maxW = Math.min(img.width,  800);
      const maxH = Math.min(img.height, 520);
      const ratio = Math.min(maxW/img.width, maxH/img.height, 1);
      cvs.width  = Math.round(img.width  * ratio);
      cvs.height = Math.round(img.height * ratio);
      redraw(img, [], cvs);
    };
    img.src = URL.createObjectURL(file);
    return () => URL.revokeObjectURL(img.src);
  // eslint-disable-next-line
  }, [file]);

  function redraw(img, arrs, cvs) {
    const c = (cvs ?? canvasRef.current);
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    arrs.forEach(a => drawArrow(ctx, a.x1, a.y1, a.x2, a.y2, a.color));
  }

  function drawArrow(ctx, x1,y1,x2,y2,col='#ef4444') {
    const headLen = 18;
    const angle   = Math.atan2(y2-y1, x2-x1);
    ctx.save();
    ctx.strokeStyle = col;
    ctx.fillStyle   = col;
    ctx.lineWidth   = 3;
    ctx.lineCap     = 'round';
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - headLen*Math.cos(angle-0.4), y2 - headLen*Math.sin(angle-0.4));
    ctx.lineTo(x2 - headLen*Math.cos(angle+0.4), y2 - headLen*Math.sin(angle+0.4));
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  function getPos(e) {
    const cvs  = canvasRef.current;
    const rect = cvs.getBoundingClientRect();
    const scaleX = cvs.width  / rect.width;
    const scaleY = cvs.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left)  * scaleX,
      y: (src.clientY - rect.top)   * scaleY,
    };
  }

  function onDown(e) {
    e.preventDefault();
    const p = getPos(e);
    setDrawing({ x1: p.x, y1: p.y });
  }

  function onMove(e) {
    e.preventDefault();
    if (!drawing || !imgEl) return;
    const p = getPos(e);
    const arrs = [...arrows, { ...drawing, x2: p.x, y2: p.y, color }];
    redraw(imgEl, arrs);
  }

  function onUp(e) {
    e.preventDefault();
    if (!drawing || !imgEl) return;
    const p = getPos(e);
    const newArr = { ...drawing, x2: p.x, y2: p.y, color };
    const arrs = [...arrows, newArr];
    setArrows(arrs);
    setDrawing(null);
    redraw(imgEl, arrs);
  }

  function undo() {
    const arrs = arrows.slice(0, -1);
    setArrows(arrs);
    if (imgEl) redraw(imgEl, arrs);
  }

  function handleDone() {
    canvasRef.current.toBlob(blob => {
      const annotated = new File([blob], 'annotated.png', { type: 'image/png' });
      onDone(annotated);
    }, 'image/png');
  }

  return (
    <div className="annotator-wrap">
      <div className="annotator-toolbar">
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>Arrow colour:</span>
        {COLORS.map(c => (
          <button
            key={c}
            onClick={() => setColor(c)}
            style={{
              width: 22, height: 22, borderRadius: '50%', background: c,
              border: color === c ? '2.5px solid var(--primary)' : '2px solid #e5e7eb',
              cursor: 'pointer', padding: 0,
            }}
          />
        ))}
        <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 12, marginLeft: 'auto' }}
          onClick={undo} disabled={arrows.length === 0}>
          ↩ Undo
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <canvas
          ref={canvasRef}
          style={{ cursor: 'crosshair', display: 'block', maxWidth: '100%', borderRadius: 8, border: '1.5px solid var(--border)' }}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp}
          onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
        />
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
        Click and drag to draw arrows pointing to what you mean.
      </div>
      <div className="annotator-actions">
        <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" onClick={handleDone}>✓ Use this image</button>
      </div>
    </div>
  );
}

// ============================================================
//  NEW POST MODAL
// ============================================================
function NewPostModal({ onClose, onPosted }) {
  const [title,      setTitle]      = useState('');
  const [body,       setBody]       = useState('');
  const [rawFile,    setRawFile]    = useState(null);   // original upload
  const [finalFile,  setFinalFile]  = useState(null);   // annotated result
  const [annotating, setAnnotating] = useState(false);
  const [preview,    setPreview]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setRawFile(f);
    setAnnotating(true);
    setFinalFile(null);
    setPreview(null);
  };

  const onAnnotationDone = (annotated) => {
    setFinalFile(annotated);
    setAnnotating(false);
    setPreview(URL.createObjectURL(annotated));
  };

  const removeImage = () => {
    setRawFile(null); setFinalFile(null); setPreview(null); setAnnotating(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!body.trim())  { toast.error('Description is required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('action', 'create');
      fd.append('title',  title.trim());
      fd.append('body',   body.trim());
      if (finalFile) fd.append('image', finalFile, 'annotated.png');
      const res = await createCommunityPost(fd);
      if (res.data?.success) {
        toast.success('Post created!');
        onPosted(res.data.pId);
      } else {
        toast.error(res.data?.message ?? 'Failed to create post');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e=>e.stopPropagation()} style={{ maxWidth: annotating ? 900 : 640 }}>
        <div className="modal-header">
          <div className="modal-title">💬 New Community Post</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {annotating ? (
            <ImageAnnotator
              file={rawFile}
              onDone={onAnnotationDone}
              onCancel={() => { setAnnotating(false); setRawFile(null); if(fileRef.current) fileRef.current.value=''; }}
            />
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Title <span style={{color:'var(--danger)'}}>*</span></label>
                <input
                  className="form-input"
                  placeholder="e.g. WhatsApp bot not responding after deploy"
                  value={title}
                  onChange={e=>setTitle(e.target.value)}
                  maxLength={200}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description <span style={{color:'var(--danger)'}}>*</span></label>
                <textarea
                  className="form-input"
                  rows={6}
                  placeholder="Describe your issue in detail. What did you expect? What happened instead?"
                  value={body}
                  onChange={e=>setBody(e.target.value)}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {/* Image section */}
              <div className="form-group">
                <label className="form-label">Screenshot (optional)</label>
                {preview ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={preview} alt="annotated" style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, border: '1.5px solid var(--border)' }} />
                    <button
                      onClick={removeImage}
                      style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >✕</button>
                    <button
                      className="btn btn-outline"
                      style={{ display: 'block', marginTop: 8, fontSize: 12 }}
                      onClick={() => setAnnotating(true)}
                    >
                      ✏️ Re-annotate
                    </button>
                  </div>
                ) : (
                  <div
                    className="upload-zone"
                    onClick={() => fileRef.current?.click()}
                  >
                    <div style={{ fontSize: 28, marginBottom: 8 }}>🖼️</div>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>Click to upload screenshot</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                      You can draw arrows to highlight the issue
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-outline" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                  {saving ? <><span className="spinner"/> Posting…</> : '📤 Post'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  POST DETAIL VIEW
// ============================================================
function PostDetail({ pId, onBack, canPost = true }) {
  const me = useContext(MeCtx);
  const [post,    setPost]    = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply,   setReply]   = useState('');
  const [sending, setSending] = useState(false);
  const [liked,   setLiked]   = useState(false);
  const [likes,   setLikes]   = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await getCommunityPost(pId);
      if (res.data?.success) {
        setPost(res.data.post);
        setReplies(res.data.replies ?? []);
        setLiked(!!res.data.post.pLiked);
        setLikes(parseInt(res.data.post.pLikeCount ?? 0));
      }
    } catch { toast.error('Failed to load post'); }
    setLoading(false);
  }, [pId]);

  useEffect(() => { load(); }, [load]);

  const handleLike = async () => {
    try {
      const res = await likeCommunityPost(pId);
      if (res.data?.success) {
        setLiked(res.data.liked);
        setLikes(res.data.count);
      }
    } catch {}
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const res = await createCommunityReply(pId, reply.trim());
      if (res.data?.success) {
        toast.success('Reply posted');
        setReply('');
        load();
      }
    } catch { toast.error('Failed to post reply'); }
    setSending(false);
  };

  const handleAccept = async (rId) => {
    try {
      await acceptCommunityReply(rId, pId);
      toast.success('Marked as solution ✓');
      load();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteReply = async (rId) => {
    try {
      await deleteCommunityReply(rId);
      setReplies(prev => prev.filter(r => r.rId !== rId));
      toast.success('Reply deleted');
    } catch { toast.error('Failed'); }
  };

  const handleResolve = async () => {
    try {
      await resolveCommunityPost(pId);
      setPost(p => ({ ...p, pStatus: 'resolved' }));
      toast.success('Marked as resolved');
    } catch {}
  };

  const handleDeletePost = async () => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await deleteCommunityPost(pId);
      toast.success('Post deleted');
      onBack();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="tab-loading"><span className="spinner spinner-dark"/> Loading…</div>;
  if (!post)   return <div className="tab-empty">Post not found.</div>;

  const isAuthor = parseInt(post.pSId) === parseInt(me);

  return (
    <div className="comm-detail">
      <button className="comm-back" onClick={onBack}>← Back to Community</button>

      {/* Post */}
      <div className="comm-post-card">
        <div className="comm-post-hdr">
          <div className="comm-avatar">{initials(post.pAuthorName)}</div>
          <div>
            <div className="comm-author">{post.pAuthorName}</div>
            <div className="comm-meta">{timeAgo(post.pCreatedAt)} · {post.pViews} views</div>
          </div>
          <span className={`comm-status-pill ${post.pStatus}`}>
            {post.pStatus === 'resolved' ? '✓ Resolved' : '● Open'}
          </span>
        </div>

        <h2 className="comm-post-title">{post.pTitle}</h2>
        <p  className="comm-post-body">{post.pBody}</p>

        {post.pImagePath && (
          <img
            src={`${IMG_BASE}/${post.pImagePath}`}
            alt="screenshot"
            className="comm-post-img"
            onClick={() => window.open(`${IMG_BASE}/${post.pImagePath}`, '_blank')}
          />
        )}

        <div className="comm-post-actions">
          <button
            className={`comm-like-btn ${liked ? 'liked' : ''}`}
            onClick={handleLike}
          >
            {liked ? '❤️' : '🤍'} {likes} {likes === 1 ? 'like' : 'likes'}
          </button>

          {isAuthor && post.pStatus === 'open' && (
            <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 12px' }} onClick={handleResolve}>
              ✓ Mark resolved
            </button>
          )}
          {isAuthor && (
            <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 12px', color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={handleDeletePost}>
              🗑 Delete
            </button>
          )}
        </div>
      </div>

      {/* Replies */}
      <div className="comm-replies-hdr">
        {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}
      </div>

      {replies.length === 0 && (
        <div className="tab-empty" style={{ marginBottom: 24 }}>
          No replies yet. Be the first to help!
        </div>
      )}

      {replies.map(r => (
        <div key={r.rId} className={`comm-reply-card ${r.rIsAccepted ? 'accepted' : ''}`}>
          {r.rIsAccepted && (
            <div className="comm-accepted-badge">✓ Accepted Solution</div>
          )}
          <div className="comm-post-hdr">
            <div className="comm-avatar sm">{initials(r.rAuthorName)}</div>
            <div>
              <div className="comm-author">{r.rAuthorName}</div>
              <div className="comm-meta">{timeAgo(r.rCreatedAt)}</div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {isAuthor && !r.rIsAccepted && post.pStatus === 'open' && (
                <button
                  className="btn btn-outline"
                  style={{ fontSize: 11.5, padding: '4px 10px', color: '#16a34a', borderColor: '#86efac' }}
                  onClick={() => handleAccept(r.rId)}
                >
                  ✓ Accept
                </button>
              )}
              {parseInt(r.rSId) === parseInt(me) && (
                <button className="icon-btn danger" title="Delete" onClick={() => handleDeleteReply(r.rId)}>✕</button>
              )}
            </div>
          </div>
          <p className="comm-reply-body">{r.rBody}</p>
        </div>
      ))}

      {/* Reply box */}
      <div className="comm-reply-box">
        <div className="comm-replies-hdr" style={{ marginBottom: 12 }}>Your Reply</div>
        {canPost ? (
          <>
            <textarea
              className="form-input"
              rows={4}
              placeholder="Share your knowledge, solution, or ask a follow-up question…"
              value={reply}
              onChange={e => setReply(e.target.value)}
              style={{ resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
              <button className="btn btn-primary" onClick={handleReply} disabled={sending || !reply.trim()}>
                {sending ? <><span className="spinner"/> Posting…</> : '💬 Post Reply'}
              </button>
            </div>
          </>
        ) : (
          <div style={{
            padding: '16px', borderRadius: 'var(--radius)',
            background: 'rgba(255,149,0,0.08)', border: '1.5px solid rgba(255,149,0,0.3)',
            textAlign: 'center', fontSize: 13, color: 'var(--text-dark)',
          }}>
            🔒 Replying requires a plan that includes Community access.{' '}
            <a href="/billing" style={{ color: 'var(--primary)', fontWeight: 700 }}>Upgrade →</a>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
//  POST CARD (list view)
// ============================================================
function PostCard({ post, onClick }) {
  return (
    <div className="comm-card" onClick={onClick}>
      <div className="comm-card-hdr">
        <div className="comm-avatar sm">{initials(post.pAuthorName)}</div>
        <span className="comm-author">{post.pAuthorName}</span>
        <span className="comm-meta">{timeAgo(post.pCreatedAt)}</span>
        <span className={`comm-status-pill ${post.pStatus}`} style={{ marginLeft: 'auto' }}>
          {post.pStatus === 'resolved' ? '✓ Resolved' : '● Open'}
        </span>
      </div>
      <div className="comm-card-title">{post.pTitle}</div>
      <div className="comm-card-body">{post.pBody.slice(0, 160)}{post.pBody.length > 160 ? '…' : ''}</div>
      {post.pImagePath && (
        <div className="comm-card-img-thumb">
          <img src={`${IMG_BASE}/${post.pImagePath}`} alt="screenshot" />
        </div>
      )}
      <div className="comm-card-footer">
        <span>💬 {post.pReplyCount} {parseInt(post.pReplyCount)===1?'reply':'replies'}</span>
        <span>❤️ {post.pLikeCount}</span>
        <span>👁 {post.pViews}</span>
      </div>
    </div>
  );
}

// ============================================================
//  MAIN PAGE
// ============================================================
export default function CommunityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view,      setView]      = useState('list');
  const [posts,     setPosts]     = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('latest');
  const [showNew,   setShowNew]   = useState(false);
  const [canPost,   setCanPost]   = useState(true);
  const LIMIT = 20;
  const [offset, setOffset] = useState(0);

  const load = useCallback(async (off=0, fil=filter) => {
    setLoading(true);
    try {
      const [postsRes, billingRes] = await Promise.allSettled([
        listCommunityPosts(LIMIT, off, fil),
        getBillingOverview(),
      ]);
      if (postsRes.status === 'fulfilled' && postsRes.value?.data?.success) {
        setPosts(postsRes.value.data.posts ?? []);
        setTotal(postsRes.value.data.total ?? 0);
      }
      if (billingRes.status === 'fulfilled') {
        const plan = billingRes.value?.data?.current_plan;
        setCanPost(!!plan?.plAllowCommunity);
      }
    } catch { toast.error('Failed to load posts'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(0, filter); }, [filter]);  // eslint-disable-line

  const changeFilter = (f) => { setFilter(f); setOffset(0); };

  const handlePosted = (pId) => {
    setShowNew(false);
    setView(pId);
  };

  return (
    <MeCtx.Provider value={user?.sId}>
      <div className="comm-page">
        {view !== 'list' ? (
          <PostDetail pId={view} canPost={canPost} onBack={() => { setView('list'); load(offset, filter); }} />
        ) : (
          <>
            {/* Header */}
            <div className="comm-page-hdr">
              <div>
                <h1 className="comm-page-title">🌐 Community</h1>
                <p className="comm-page-sub">Ask questions, share solutions, help fellow developers.</p>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => canPost ? setShowNew(true) : navigate('/billing')}
                title={!canPost ? 'Upgrade your plan to post in the community' : ''}
              >
                {canPost ? '+ New Post' : '🔒 Upgrade to Post'}
              </button>
            </div>

            {!canPost && (
              <div style={{
                marginBottom: 16, padding: '12px 16px', borderRadius: 'var(--radius)',
                background: 'rgba(255,149,0,0.08)', border: '1.5px solid rgba(255,149,0,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              }}>
                <div style={{ fontSize: 13, color: 'var(--text-dark)' }}>
                  🔒 <strong>Community posting</strong> is not included in your current plan.
                  You can read posts but cannot post or reply.
                </div>
                <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px', flexShrink: 0 }}
                  onClick={() => navigate('/billing')}>
                  Upgrade
                </button>
              </div>
            )}

            {/* Filters */}
            <div className="comm-filters">
              {[['latest','🕐 Latest'],['popular','🔥 Popular'],['open','🟢 Open']].map(([k,l]) => (
                <button
                  key={k}
                  className={`comm-filter-btn ${filter===k?'active':''}`}
                  onClick={() => changeFilter(k)}
                >{l}</button>
              ))}
              <span style={{ marginLeft: 'auto', fontSize: 12.5, color: 'var(--text-muted)' }}>
                {total} post{total!==1?'s':''}
              </span>
            </div>

            {/* List */}
            {loading ? (
              <div className="tab-loading"><span className="spinner spinner-dark"/> Loading…</div>
            ) : posts.length === 0 ? (
              <div className="tab-empty">
                No posts yet.{' '}
                <button className="link-btn" onClick={() => canPost ? setShowNew(true) : navigate('/billing')}>Be the first to post →</button>
              </div>
            ) : (
              <>
                <div className="comm-list">
                  {posts.map(p => (
                    <PostCard key={p.pId} post={p} onClick={() => setView(p.pId)} />
                  ))}
                </div>

                {/* Pagination */}
                {total > LIMIT && (
                  <div className="comm-pagination">
                    <button
                      className="btn btn-outline"
                      disabled={offset === 0}
                      onClick={() => { const o = Math.max(0, offset-LIMIT); setOffset(o); load(o, filter); }}
                    >← Prev</button>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      {Math.floor(offset/LIMIT)+1} / {Math.ceil(total/LIMIT)}
                    </span>
                    <button
                      className="btn btn-outline"
                      disabled={offset + LIMIT >= total}
                      onClick={() => { const o = offset+LIMIT; setOffset(o); load(o, filter); }}
                    >Next →</button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {showNew && (
          <NewPostModal onClose={() => setShowNew(false)} onPosted={handlePosted} />
        )}
      </div>
    </MeCtx.Provider>
  );
}